import React, { useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import EmptyCardDealWorkspace from "assets/images/EmptyCardDealWorkspace.png";
import { WorkspaceDocumentIcon, WorkspaceFolderCloseIcon, WorkspaceFormIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { DealRoleGuard } from "components/guards";
import { FileViewer, Menu } from "components/popups";
import { Notice } from "components/Prompts/Notice";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import {
  useActionHook,
  useDealParamsHooks,
  useHighlightAfterClose,
  useIsViewerPermission,
  useModals,
  usePrompt
} from "hooks";
import { useNavigate } from "hooks";
import {
  useAllFilesSelect,
  useDealActions,
  useDealStatusSelector,
  useOpportunityIdSelector,
  usePickUserPermissions
} from "store";
import { FolderKind, IFile, IFileMetaDetail, IFileRepo, IMetaDetails, UserPermissionType } from "store/types";

import { ClassificationPrompt } from "../../components/File-repository/Table/ClassificationPrompt";
import { CurrencyPrompt } from "../../components/File-repository/Table/CurrencyPrompt";
import { EffectiveDatePrompt } from "../../components/File-repository/Table/EffectiveDatePrompt";
import { InvestorPrompt } from "../../components/File-repository/Table/InvestorPrompt";
import { StatusPrompt } from "../../components/File-repository/Table/StatusPrompt";
import { EntityType, FileType, RoleName } from "../../constants";
import { useDealWorkspace } from "./context/DealWorkspace-context";
import { useBulkActionsHook } from "./Hooks/use-bulk-actions-hook";
import { useFolderHook } from "./Hooks/use-folder-hook";
import { useMetadataTableDrag, useWorkspaceFileDrop } from "./Hooks/use-workspace-dnd";
import { useWorkspaceFilter } from "./Hooks/use-workspace-filter";
import { ColumnVisibilityMenu } from "./Menus/ColumnVisibilityMenu";
import { FolderActions } from "./Menus/FolderActions";
import { BreadCrumbs } from "./Workspace-metadata-components/BreadCrumbs";
import { BulkActionsBar } from "./Workspace-metadata-components/BulkActionsBar";
import { MetadataTable } from "./Workspace-metadata-components/MetadataTable";
import { Share } from "./Workspace-metadata-components/Share";
import { WorkspaceFilter } from "./Workspace-metadata-components/WorkspaceFilter";

const PREVIEW_TYPES = [FileType.PDF, FileType.JPG, FileType.JPEG, FileType.PNG];

export const WorkspaceMetadata = () => {
  const {
    folderMeta: folderMetaFromContext,
    folderMetadataLoading,
    handleFetchNextPage,
    setSortBy,
    columnVisibility,
    columnOrder,
    currentFolder,
    folderTitle,
    toggleColumn,
    onFolderClick,
    activeFilters,
    handleChangeFilter,
    selectedItems,
    setSelectedItems,
    refetchFolderMetadata,
    reorderColumns,
    getWorkspaceFilters,
    workspaceFiltersFromDB,
    isAggregateView
  } = useDealWorkspace();

  const { handleBulkDelete, handleBulkDownload } = useBulkActionsHook({
    selectedItems,
    setSelectedItems
  });
  const openPrompt = usePrompt();
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const { noticeModal, errorModal } = useModals();
  const {
    uploadDirectoryAction,
    getFoldersAction,
    getFilesAction,
    patchFolderMetaDetailAction,
    downloadFileFromDRAction,
    bulkUpdateWorkspaceFilesAction
  } = useDealActions();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;
  const opportunityId = useOpportunityIdSelector();
  const status = useDealStatusSelector();
  const directoryInputRef = useRef<HTMLInputElement | null>(null);
  const { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop } = useWorkspaceFileDrop();
  const { getRowDragHandlers } = useMetadataTableDrag(currentFolder?.kind as FolderKind, refetchFolderMetadata);

  const { filtersList, dataroomOnlyKeys, isDataroom, filterTypes } = useWorkspaceFilter({
    currentFolder,
    activeFilters,
    handleChangeFilter,
    workspaceFiltersFromDB
  });

  const files = useAllFilesSelect();

  const [file, setFile] = useState<IFile | null>(null);
  const {
    highlightedId: highlightedItemId,
    lastViewedIdRef: lastViewedFileIdRef,
    triggerHighlight
  } = useHighlightAfterClose();

  const navigate = useNavigate();
  const { dealId, sectionId } = useDealParamsHooks();

  const { onCreateNewFolder, onFileUpload, onAddForm, onAddDocument } = useFolderHook({
    folder: {
      id: currentFolder?.id || -1,
      title: currentFolder?.title || "",
      is_private: currentFolder?.is_private || false,
      parent_id: currentFolder?.parent_id || null,
      deal_id: dealId,
      user_id: currentFolder?.user_id || -1,
      kind: currentFolder?.kind || "folder"
    },
    refetchHandler: refetchFolderMetadata
  });

  const { action: uploadDirectory } = useActionHook(uploadDirectoryAction, {
    onSuccess: () => {
      if (dealId) {
        getFoldersAction({ dealId });
        getFilesAction(dealId);
        refetchFolderMetadata();
      }
    },
    onReject: () => {
      errorModal({ msg: "Something went wrong" });
    }
  });

  useEffect(() => {
    if (!directoryInputRef.current) return;
    directoryInputRef.current.setAttribute("directory", "");
    directoryInputRef.current.setAttribute("webkitdirectory", "");
  }, [directoryInputRef]);

  const { title, details, shared_with, is_private, kind } = folderMetaFromContext;

  const search = useMemo(() => (sectionId ? { section: String(sectionId) } : undefined), [sectionId]);

  const handleRowClick = async (row: IMetaDetails) => {
    if (
      row.entity_type === EntityType.FILE &&
      (row as IFileMetaDetail).dataroom_file_id &&
      (row as IFileMetaDetail).file_url === ""
    ) {
      const syncedFile = await downloadFileFromDRAction({ dealId, fileId: row.id });
      patchFolderMetaDetailAction({
        key: "patch",
        id: row.id,
        entityType: EntityType.FILE,
        patch: { file_url: syncedFile.file_url ?? "" }
      });
      return;
    }

    if (row.type === EntityType.FOLDER) {
      onFolderClick({ folderId: row.id, isCloudFolder: false });
      return;
    }

    if (row.type === FileType.PDF) {
      openPrompt({
        componentType: "fullscreen",
        Component: DataExtractionModal,
        props: {
          fileIds: [row.id],
          fileNames: { [row.id]: row.name },
          source: "deal" as const,
          initialShowParameters: false,
          onUpdateFile: (fileId: number, updates: Partial<IFileRepo>) => {
            patchFolderMetaDetailAction({
              key: "patch",
              id: fileId,
              entityType: EntityType.FILE,
              patch: updates
            });
          }
        }
      });
      return;
    }
    if (PREVIEW_TYPES.includes(row.type as FileType)) {
      const foundFile = files.find((file: IFile) => file.id === row.id);
      if (foundFile) setFile(foundFile);
      return;
    }
    switch (row.type) {
      case EntityType.FORM:
        navigate({ route: "dealForm", params: { deal_id: dealId, form_id: row.id }, search });
        break;
      case EntityType.DOCUMENT:
        navigate({ route: "dealDocument", params: { deal_id: dealId, document_id: row.id }, search });
        break;
      default:
        navigate({ route: "dealFile", params: { deal_id: dealId, file_id: row.id }, search });
        break;
    }
  };

  const handleBulkDataExtractor = async () => {
    const fileItems = selectedItems.filter(
      (item) => item.entity_type === "file" && item.type === FileType.PDF
    ) as IFileMetaDetail[];
    if (!fileItems.length) return;

    const fileIds = fileItems.map((item) => item.id);
    const fileNames = fileItems.reduce(
      (acc, item) => {
        acc[item.id] = item.name;
        return acc;
      },
      {} as Record<number, string>
    );

    lastViewedFileIdRef.current = fileIds[0];

    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds,
        fileNames,
        source: "deal" as const,
        initialShowParameters: false,
        onUpdateFile: (fileId: number, updates: Partial<IFileRepo>) => {
          patchFolderMetaDetailAction({
            key: "patch",
            id: fileId,
            entityType: EntityType.FILE,
            patch: updates
          });
        },
        onCloseWithFileId: (currentFileId: number) => {
          lastViewedFileIdRef.current = currentFileId;
        }
      }
    });

    triggerHighlight();
  };

  const handleBulkView = async () => {
    const fileItems = selectedItems.filter(
      (item) => item.entity_type === "file" && item.type === FileType.PDF
    ) as IFileMetaDetail[];
    if (!fileItems.length) return;

    const fileIds = fileItems.map((item) => item.id);
    const fileNames = fileItems.reduce(
      (acc, item) => {
        acc[item.id] = item.name;
        return acc;
      },
      {} as Record<number, string>
    );

    lastViewedFileIdRef.current = fileIds[0];

    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds,
        fileNames,
        initialShowParameters: false,
        source: "deal" as const,
        onUpdateFile: (fileId: number, updates: Partial<IFileRepo>) => {
          patchFolderMetaDetailAction({
            key: "patch",
            id: fileId,
            entityType: EntityType.FILE,
            patch: updates
          });
        },
        onCloseWithFileId: (currentFileId: number) => {
          lastViewedFileIdRef.current = currentFileId;
        }
      }
    });

    triggerHighlight();
  };

  const handleChangeClassification = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) {
      noticeModal({ msg: "Please select at least one file." });
      return;
    }

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: ClassificationPrompt,
      props: {
        currentClassification: null
      }
    });

    if (!result) return;

    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      document_type: result.classification
    });

    updatedFiles.forEach((updatedFile) => {
      patchFolderMetaDetailAction({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const handleChangeCurrency = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) {
      noticeModal({ msg: "Please select at least one file." });
      return;
    }

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: CurrencyPrompt,
      props: {
        currentCurrency: null
      }
    });

    if (!result) return;

    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      currency: result.currency
    });

    updatedFiles.forEach((updatedFile) => {
      patchFolderMetaDetailAction({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const handleChangeInvestor = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) {
      noticeModal({ msg: "Please select at least one file." });
      return;
    }

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: InvestorPrompt,
      props: {
        currentInvestorId: null,
        currentAllInvestors: false,
        currentSplitByCommitment: false,
        dealId: null,
        showGrouped: false
      }
    });

    if (!result) return;

    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      investor_id: result.investor?.id ?? null,
      split_by_commitment: result.splitByCommitment
    });

    updatedFiles.forEach((updatedFile) => {
      patchFolderMetaDetailAction({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const handleChangeEffectiveDate = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) {
      noticeModal({ msg: "Please select at least one file." });
      return;
    }

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: EffectiveDatePrompt,
      props: {
        currentDate: null
      }
    });

    if (!result) return;

    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      effective_date: result.effectiveDate
    });

    updatedFiles.forEach((updatedFile) => {
      patchFolderMetaDetailAction({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  const handleChangeStatus = async (anchorEl: HTMLElement | null) => {
    const fileItems = selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
    if (!fileItems.length) {
      noticeModal({ msg: "Please select at least one file." });
      return;
    }

    const result = await openPrompt({
      componentType: "popover",
      ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
      Component: StatusPrompt,
      props: {
        currentStatus: "new"
      }
    });

    if (!result) return;

    const updatedFiles = await bulkUpdateWorkspaceFilesAction({
      file_ids: fileItems.map((f) => f.id),
      file_status: result.status
    });

    updatedFiles.forEach((updatedFile) => {
      patchFolderMetaDetailAction({
        key: "patch",
        id: updatedFile.id,
        entityType: EntityType.FILE,
        patch: updatedFile
      });
    });
  };

  return (
    <Stack direction="column" sx={{ width: "100%", height: "100%", overflow: "hidden" }} gap="16px">
      <Stack direction="column" gap="16px">
        {currentFolder?.id && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <BreadCrumbs showCurrentFolder />
            {kind !== "dataroom" && (
              <Share
                currentFolder={currentFolder}
                shared_with={shared_with || []}
                details={details || []}
                is_private={is_private || false}
                title={title || ""}
              />
            )}
          </Stack>
        )}
        <Stack direction="row" alignItems="center" gap="23px" sx={{ width: "100%" }}>
          <Stack direction="row" alignItems="center" gap="10px" sx={{ flex: 1, minWidth: 0 }}>
            <WorkspaceFolderCloseIcon sx={{ fill: "#0A3142", fontSize: 20 }} />
            <Typography
              noWrap
              sx={{
                color: "#0a3142",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px"
              }}
            >
              {folderTitle || ""}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap="8px" sx={{ flexShrink: 0 }}>
            <ColumnVisibilityMenu
              columnVisibility={columnVisibility}
              onToggle={toggleColumn}
              kind={kind as FolderKind}
              showDataroomColumns={isAggregateView}
              hideShareColumn={kind === "dataroom"}
            />
            {currentFolder?.id && <FolderActions folder={currentFolder} refetchHandler={refetchFolderMetadata} />}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" gap="10px" justifyContent="space-between">
          <WorkspaceFilter
            handleChangeFilter={handleChangeFilter}
            filterTypes={filterTypes}
            filtersList={filtersList}
            applyMode={true}
            state={activeFilters}
            isDataroom={isDataroom}
            dataroomOnlyKeys={dataroomOnlyKeys}
            getFiltersOptions={() => getWorkspaceFilters(dealId)}
            defaultFilterKeys={["document_type", "file_type", "investor"]}
          />
          <BulkActionsBar
            selectedItems={selectedItems}
            onClose={() => setSelectedItems([])}
            onView={handleBulkView}
            onExtract={handleBulkDataExtractor}
            onDownload={handleBulkDownload}
            onChangeClassification={handleChangeClassification}
            onChangeCurrency={handleChangeCurrency}
            onChangeInvestor={handleChangeInvestor}
            onChangeEffectiveDate={handleChangeEffectiveDate}
            onChangeStatus={handleChangeStatus}
            onDelete={handleBulkDelete}
          />
        </Stack>
      </Stack>
      <Stack
        direction="column"
        sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", position: "relative" }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              border: "2px dashed #5B9BD5",
              borderRadius: "8px",
              backgroundColor: "rgba(200, 220, 240, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none"
            }}
          >
            <Typography sx={{ color: "#0A3142", fontWeight: 600, fontSize: 16 }}>Drop files to upload</Typography>
          </Box>
        )}
        {details?.length || folderMetadataLoading || !!Object.keys(activeFilters).length ? (
          <MetadataTable
            kind={kind as FolderKind}
            data={details || []}
            isLoading={folderMetadataLoading}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            onRowClick={handleRowClick}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            reorderColumns={reorderColumns}
            refetchFolderMetadata={refetchFolderMetadata}
            getRowDragHandlers={getRowDragHandlers}
            onFetchNextPage={handleFetchNextPage}
            showDataroomColumns={isAggregateView}
            manualSorting
            onSortingChange={setSortBy}
            hideShareColumn={kind === "dataroom"}
            highlightedItemId={highlightedItemId}
          />
        ) : currentFolder ? (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.workspaceFolder.title}
            description={{
              text: EMPTY_CARD_CONTENT.workspaceFolder.description,
              externalText: EMPTY_CARD_CONTENT.workspaceFolder.externalDescription
            }}
            image={{
              src: EmptyCardDealWorkspace,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <DealRoleGuard
                availableRoles={[RoleName.Admin, RoleName.Analyst]}
                hideChild={!!noAccessPermission || !!isViewerPermission}
              >
                <Menu
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  hidden={
                    status?.id === opportunityId ||
                    !currentFolder ||
                    currentFolder.kind === "dataroom" ||
                    !!Object.keys(activeFilters).length
                  }
                  icon={
                    <Button
                      sx={{ textTransform: "none", bgcolor: "#071F35" }}
                      startIcon={<AddIcon />}
                      variant="contained"
                    >
                      Add item
                    </Button>
                  }
                  keepTriggerEnabled
                  iconButtonProps={{ sx: { padding: 0, width: "auto" } }}
                  options={[
                    {
                      title: "New folder",
                      icon: <WorkspaceFolderCloseIcon sx={{ fill: "#5d7782" }} />,
                      onClick: () => onCreateNewFolder?.(),
                      disabled: !onCreateNewFolder
                    },
                    {
                      title: "New document",
                      icon: <WorkspaceDocumentIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onAddDocument?.(),
                      disabled: !onAddDocument
                    },
                    {
                      title: "New form",
                      icon: <WorkspaceFormIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onAddForm?.(),
                      disabled: !onAddForm
                    },
                    {
                      title: "Upload file",
                      icon: <FileUploadOutlinedIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onFileUpload?.(),
                      disabled: !onFileUpload
                    }
                  ]}
                />
              </DealRoleGuard>
            }
          />
        ) : (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.workspaceEmpty.title}
            description={{
              text: EMPTY_CARD_CONTENT.workspaceEmpty.description,
              externalText: EMPTY_CARD_CONTENT.workspaceEmpty.externalDescription,
              sx: { width: 388 }
            }}
            image={{
              src: EmptyCardDealWorkspace,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <>
                <Menu
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  icon={
                    <Button
                      sx={{ textTransform: "none", bgcolor: "#071F35" }}
                      startIcon={<AddIcon />}
                      variant="contained"
                    >
                      Add item
                    </Button>
                  }
                  hidden={noAccessPermission || isViewerPermission || !!Object.keys(activeFilters).length}
                  keepTriggerEnabled
                  iconButtonProps={{ sx: { padding: 0, width: "auto" } }}
                  options={[
                    {
                      title: "Upload File",
                      icon: <FileUploadOutlinedIcon />,
                      onClick: onFileUpload,
                      hasLine: false
                    },
                    {
                      title: "Upload Folder to Workspace",
                      icon: <FileUploadOutlinedIcon />,
                      onClick: () => {
                        if (isAnalystViewerPermission) {
                          openPrompt({ Component: Notice, props: { type: "trial" } });
                          return;
                        }
                        if (!directoryInputRef.current) return;
                        directoryInputRef.current.click();
                      },
                      hasLine: false
                    },
                    {
                      title: "New Folder",
                      icon: <CreateNewFolderOutlinedIcon />,
                      onClick: onCreateNewFolder
                    }
                  ]}
                />
                <input
                  ref={directoryInputRef}
                  type="file"
                  onChange={(e) => {
                    if (!e.target.files || !dealId) return;
                    uploadDirectory({ deal_id: dealId, files: e.target.files });
                  }}
                  {...{ webkitdirectory: "" }}
                  hidden
                  style={{ width: 0, height: 0 }}
                />
              </>
            }
          />
        )}

        <FileViewer file={file as IFile} isOpen={Boolean(file)} onClose={() => setFile(null)} />
      </Stack>
    </Stack>
  );
};
